// Cockpit — the exec landing page.
//
// One-screen answer to "what does the horizon look like right now and what
// moved this period." Three blocks stacked top-to-bottom:
//
//   1. 3×3 heatmap. Outside (Tech/Industry/Society) × Inside (Client/Field/
//      Enterprise). Cell intensity = aggregate of the topics that touch both
//      that macro and that lens. Click a cell to drill into Map filtered to
//      that intersection.
//   2. Top movers strip. Three columns: positive, negative, new. Pulled from
//      the `score_movements` view at the active period scope.
//   3. Counters: total topics, h3 thesis, transformation-level breakdown.
//
// Built entirely on existing queries — no new SQL — so this lands without a
// schema migration. The DB-backed monthly snapshots come later; the time
// scrubber is a placeholder UI for now (renders, doesn't yet rewind).

import {
  composeTopic,
  loadGraph,
  loadMovements,
  type PeriodScope,
} from "../lib/queries.ts";
import {
  iterCells,
  LENS_LABEL,
  lensOfBusinessLine,
  MACRO_LABEL,
  macroOfCategory,
  type Lens,
  type Macro,
} from "../lib/macros.ts";
import { navigate } from "../lib/router.ts";
import type { ScoreMovement, TopicWithContext } from "../lib/types.ts";

interface CellAgg {
  macro: Macro;
  lens: Lens;
  topicCount: number;
  /** Mean of (readiness + relevance) / 2 across all qualifying topics. 0–3 scale. */
  intensity: number;
  /** Top 3 topic IDs by intensity, used for the cell drill-in tooltip. */
  topTopicIds: number[];
}

function aggregateCells(
  ctxs: TopicWithContext[],
  catToMacro: Map<number, Macro>,
  blToLens: Map<number, Lens>,
): CellAgg[] {
  // Bucket: macro|lens → { sum, count, topics: [{id, score}] }
  const buckets = new Map<string, { sum: number; count: number; topics: Array<{ id: number; score: number }> }>();
  for (const ctx of ctxs) {
    // Outside macros this topic touches (via its category memberships).
    const macros = new Set<Macro>();
    for (const m of ctx.memberships) {
      const mc = catToMacro.get(m.category.id);
      if (mc) macros.add(mc);
    }
    // Inside lenses where this topic has any non-null relevance.
    const lensScores: Record<Lens, number[]> = { client: [], field: [], enterprise: [] };
    for (const [blKey, rel] of Object.entries(ctx.relevance)) {
      if (rel.score == null) continue;
      // We need the BL id to look up lens; we don't have it here, so map by key.
      const lens = LENS_BY_KEY[blKey] ?? null;
      if (!lens) continue;
      lensScores[lens].push(rel.score);
    }

    const readiness = ctx.readiness?.avg_readiness_v1 ?? null;

    for (const macro of macros) {
      for (const lens of (Object.keys(lensScores) as Lens[])) {
        const lensVals = lensScores[lens];
        if (lensVals.length === 0) continue;
        const lensMean = lensVals.reduce((a, b) => a + b, 0) / lensVals.length;
        const cellScore = readiness != null ? (readiness + lensMean) / 2 : lensMean;
        const k = `${macro}|${lens}`;
        const b = buckets.get(k) ?? { sum: 0, count: 0, topics: [] };
        b.sum += cellScore;
        b.count += 1;
        b.topics.push({ id: ctx.topic.id, score: cellScore });
        buckets.set(k, b);
      }
    }
    // Avoid unused-var lint until blToLens is wired by id (we resolve by key above).
    void blToLens;
  }

  const cells: CellAgg[] = [];
  for (const { macro, lens } of iterCells()) {
    const k = `${macro}|${lens}`;
    const b = buckets.get(k);
    if (!b || b.count === 0) {
      cells.push({ macro, lens, topicCount: 0, intensity: 0, topTopicIds: [] });
      continue;
    }
    b.topics.sort((a, c) => c.score - a.score);
    cells.push({
      macro,
      lens,
      topicCount: b.count,
      intensity: b.sum / b.count,
      topTopicIds: b.topics.slice(0, 3).map((t) => t.id),
    });
  }
  return cells;
}

// Built lazily on first cell aggregation, then reused. Cleared on re-render.
let LENS_BY_KEY: Record<string, Lens> = {};

export function renderCockpit(
  host: HTMLElement,
  period: PeriodScope,
  onPinTopic: (topicId: number) => void,
): () => void {
  host.innerHTML = `
    <div class="cockpit">
      <div class="cockpit-loading muted">Loading horizon…</div>
    </div>
  `;

  let cancelled = false;

  (async () => {
    try {
      const [graph, movements] = await Promise.all([loadGraph(), loadMovements(period)]);
      if (cancelled) return;

      const catToMacro = new Map<number, Macro>();
      for (const c of graph.categories) catToMacro.set(c.id, macroOfCategory(c));
      const blToLens = new Map<number, Lens>();
      LENS_BY_KEY = {};
      for (const bl of graph.businessLines) {
        const lens = lensOfBusinessLine(bl);
        blToLens.set(bl.id, lens);
        LENS_BY_KEY[bl.key] = lens;
      }

      const ctxs = graph.topics
        .map((t) => composeTopic(t.id, graph)!)
        .filter(Boolean);

      const cells = aggregateCells(ctxs, catToMacro, blToLens);
      const maxIntensity = Math.max(0.01, ...cells.map((c) => c.intensity));

      // ---------- Counters ----------
      const totalTopics = graph.topics.length;
      const h3 = graph.topics.filter((t) => t.h3_thesis_flag).length;
      const curated = graph.topics.filter((t) => !t.ai_drafted).length;
      const ai = totalTopics - curated;

      // ---------- Top movers ----------
      const sortedMovers = [...movements].sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));
      const positives = sortedMovers.filter((m) => m.delta > 0).slice(0, 5);
      const negatives = sortedMovers.filter((m) => m.delta < 0).slice(0, 5);

      // Topics whose first capture/score arrived inside this period — proxy
      // for "new" until we have a created_at filter on topics. For now use
      // the size of the movers set as a noisy proxy.
      const newTopics = graph.topics
        .filter((t) => t.ai_drafted)
        .sort((a, b) => b.id - a.id)
        .slice(0, 5);

      // ---------- HTML ----------
      const cellByKey = new Map<string, CellAgg>();
      for (const c of cells) cellByKey.set(`${c.macro}|${c.lens}`, c);
      const cellHTML = (macro: Macro, lens: Lens): string => {
        const c = cellByKey.get(`${macro}|${lens}`)!;
        const opacity = c.topicCount === 0 ? 0.1 : 0.25 + 0.75 * (c.intensity / maxIntensity);
        const intensityLabel = c.topicCount === 0 ? "—" : c.intensity.toFixed(2);
        return `
          <button class="cockpit-cell" data-macro="${c.macro}" data-lens="${c.lens}"
                  title="${MACRO_LABEL[c.macro]} × ${LENS_LABEL[c.lens]} · ${c.topicCount} topics · intensity ${intensityLabel}">
            <span class="cockpit-cell-fill" style="opacity: ${opacity.toFixed(3)}"></span>
            <span class="cockpit-cell-body">
              <span class="cockpit-cell-count">${c.topicCount}</span>
              <span class="cockpit-cell-intensity">${intensityLabel}</span>
            </span>
          </button>
        `;
      };

      const moverRow = (m: ScoreMovement, sign: "+" | "−") => `
        <li class="cockpit-mover" data-topic-id="${m.topic_id}">
          <span class="cockpit-mover-sign cockpit-mover-${sign === "+" ? "up" : "down"}">${sign}${Math.abs(m.delta)}</span>
          <span class="cockpit-mover-body">
            <span class="cockpit-mover-name">${escapeHtml(m.topic_name)}</span>
            <span class="cockpit-mover-meta">${escapeHtml(m.metric_name)} · ${m.score_prev ?? "—"} → ${m.score_now}</span>
          </span>
        </li>
      `;

      host.innerHTML = `
        <div class="cockpit">
          <section class="cockpit-grid-wrap">
            <header class="cockpit-section-head">
              <h3>Horizon × Impact</h3>
              <span class="muted">outside macros × inside lenses · click to drill</span>
            </header>
            <div class="cockpit-grid">
              <div class="cockpit-grid-corner"></div>
              <div class="cockpit-grid-col-head">${LENS_LABEL.client}</div>
              <div class="cockpit-grid-col-head">${LENS_LABEL.field}</div>
              <div class="cockpit-grid-col-head">${LENS_LABEL.enterprise}</div>

              <div class="cockpit-grid-row-head">${MACRO_LABEL.tech}</div>
              ${cellHTML("tech", "client")}${cellHTML("tech", "field")}${cellHTML("tech", "enterprise")}

              <div class="cockpit-grid-row-head">${MACRO_LABEL.industry}</div>
              ${cellHTML("industry", "client")}${cellHTML("industry", "field")}${cellHTML("industry", "enterprise")}

              <div class="cockpit-grid-row-head">${MACRO_LABEL.society}</div>
              ${cellHTML("society", "client")}${cellHTML("society", "field")}${cellHTML("society", "enterprise")}
            </div>
          </section>

          <section class="cockpit-counters">
            <div class="cockpit-counter">
              <span class="cockpit-counter-value">${totalTopics}</span>
              <span class="cockpit-counter-label">topics tracked</span>
            </div>
            <div class="cockpit-counter">
              <span class="cockpit-counter-value">${h3}</span>
              <span class="cockpit-counter-label">h3 thesis</span>
            </div>
            <div class="cockpit-counter">
              <span class="cockpit-counter-value">${curated}</span>
              <span class="cockpit-counter-label">curated</span>
            </div>
            <div class="cockpit-counter">
              <span class="cockpit-counter-value">${ai}</span>
              <span class="cockpit-counter-label">AI-drafted</span>
            </div>
          </section>

          <section class="cockpit-movers">
            <header class="cockpit-section-head">
              <h3>What moved</h3>
              <span class="muted">period · ${period}</span>
            </header>
            <div class="cockpit-mover-cols">
              <div class="cockpit-mover-col">
                <h4 class="cockpit-mover-col-head cockpit-mover-col-up">↑ Rising</h4>
                <ul class="cockpit-mover-list">
                  ${positives.length ? positives.map((m) => moverRow(m, "+")).join("") : `<li class="cockpit-mover-empty muted">No rises this period.</li>`}
                </ul>
              </div>
              <div class="cockpit-mover-col">
                <h4 class="cockpit-mover-col-head cockpit-mover-col-down">↓ Falling</h4>
                <ul class="cockpit-mover-list">
                  ${negatives.length ? negatives.map((m) => moverRow(m, "−")).join("") : `<li class="cockpit-mover-empty muted">No drops this period.</li>`}
                </ul>
              </div>
              <div class="cockpit-mover-col">
                <h4 class="cockpit-mover-col-head cockpit-mover-col-new">+ New</h4>
                <ul class="cockpit-mover-list">
                  ${
                    newTopics.length
                      ? newTopics
                          .map(
                            (t) => `
                            <li class="cockpit-mover" data-topic-id="${t.id}">
                              <span class="cockpit-mover-sign cockpit-mover-up">+</span>
                              <span class="cockpit-mover-body">
                                <span class="cockpit-mover-name">${escapeHtml(t.name)}</span>
                                <span class="cockpit-mover-meta">${t.h3_thesis_flag ? "h3 thesis" : "tracked"}</span>
                              </span>
                            </li>
                          `,
                          )
                          .join("")
                      : `<li class="cockpit-mover-empty muted">No new topics this period.</li>`
                  }
                </ul>
              </div>
            </div>
          </section>
        </div>
      `;

      // Click handlers
      host.querySelectorAll<HTMLElement>(".cockpit-cell").forEach((el) => {
        el.addEventListener("click", () => {
          const macro = el.dataset.macro as Macro;
          const lens = el.dataset.lens as Lens;
          // Drill into Map with the cell as a filter via URL hash.
          location.hash = `#/cluster?macro=${macro}&lens=${lens}`;
        });
      });
      host.querySelectorAll<HTMLElement>(".cockpit-mover").forEach((el) => {
        el.addEventListener("click", () => {
          const id = Number(el.dataset.topicId);
          if (id) onPinTopic(id);
        });
      });
    } catch (e) {
      if (cancelled) return;
      host.innerHTML = `<div class="viz-empty">Cockpit load failed: ${escapeHtml(String(e))}</div>`;
    }
  })();

  // Suppress unused-import nag in some toolchains.
  void navigate;

  return () => {
    cancelled = true;
    host.innerHTML = "";
  };
}

function escapeHtml(s: string): string {
  return s
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}
